import React, { useState, useEffect, useCallback } from "react";
import {
  getAlerts, createAlert, deleteAlert, toggleAlert,
  getAlertHistory, runAlertsCheck, syncTelegram, sendTelegramTest,
  autoSetupAlerts, deleteAutoAlerts, updateNotificationPreferences,
} from "../lib/api";
import {
  Bell, Plus, Trash2, TrendingUp, CalendarClock, Mail,
  Send, Check, X, Clock, PlayCircle, RefreshCcw, Target, Activity, Wand2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const CHANNEL_LABELS = { email: "Email", telegram: "Telegram" };

const TYPE_META = {
  pct_move: {
    label: "% Move",
    icon: TrendingUp,
    threshold_suffix: "%",
    default_threshold: 5,
    direction: true,
    direction_options: [
      { value: "both", label: "Either direction" },
      { value: "up", label: "Up only" },
      { value: "down", label: "Down only" },
    ],
    symbol_required: true,
    description: "Triggers when a stock moves ± by your threshold vs previous close.",
  },
  earnings_reminder: {
    label: "Earnings Reminder",
    icon: CalendarClock,
    threshold_suffix: " days",
    default_threshold: 7,
    direction: false,
    symbol_required: true,
    description: "Alerts when earnings date is within N days.",
  },
  price_target: {
    label: "Price Target",
    icon: Target,
    threshold_suffix: "",
    default_threshold: 0,
    direction: true,
    direction_options: [
      { value: "above", label: "Above target" },
      { value: "below", label: "Below target" },
    ],
    symbol_required: true,
    needs_target_price: true,
    description: "Triggers when the stock's LTP crosses an absolute ₹ price level.",
  },
  score_change: {
    label: "Score Change",
    icon: Activity,
    threshold_suffix: "",
    default_threshold: 0,
    direction: false,
    symbol_required: true,
    description: "Triggers when Tickertape scorecard tags change (Entry Point, Red Flags, etc.)",
  },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AlertsPage() {
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [telegram, setTelegram] = useState({ bot_username: "", chat_ids: [], linked: false });
  const [preferences, setPreferences] = useState(null);
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoSetting, setAutoSetting] = useState(false);

  // Form state
  const [type, setType] = useState("pct_move");
  const [symbol, setSymbol] = useState("");
  const [threshold, setThreshold] = useState(5);
  const [direction, setDirection] = useState("both");
  const [targetPrice, setTargetPrice] = useState("");
  const [telegramEnabled, setTelegramEnabled] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await getAlerts();
      setRules(data.rules || []);
      setTelegram(data.telegram || {});
      setPreferences(data.notification_preferences || null);
    } catch {
      toast.error("Failed to load alerts");
    }
    try {
      const { data: h } = await getAlertHistory(25);
      setHistory(h.history || []);
    } catch (err) {
      // history failure shouldn't block
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setSymbol("");
    setThreshold(TYPE_META[type]?.default_threshold ?? 5);
    setDirection("both");
    setTargetPrice("");
  };

  const onTypeChange = (t) => {
    setType(t);
    setThreshold(TYPE_META[t]?.default_threshold ?? 5);
    // Reset direction to valid default per type
    const m = TYPE_META[t];
    if (m?.direction_options?.length) {
      setDirection(m.direction_options[0].value);
    } else {
      setDirection("both");
    }
  };

  const handleCreate = async () => {
    const meta = TYPE_META[type];
    if (meta.symbol_required && !symbol.trim()) {
      toast.error("Symbol is required");
      return;
    }
    if (meta.needs_target_price) {
      const tp = parseFloat(targetPrice);
      if (!tp || tp <= 0) {
        toast.error("Enter a valid target price (> 0)");
        return;
      }
    }
    const channels = ["telegram"];
    try {
      await createAlert({
        symbol: symbol.trim().toUpperCase() || null,
        type,
        threshold_pct: parseFloat(threshold) || meta.default_threshold,
        direction: meta.direction ? direction : "both",
        target_price: meta.needs_target_price ? parseFloat(targetPrice) : null,
        channels,
      });
      toast.success("Alert created");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAlert(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleToggle = async (id, next) => {
    try {
      await toggleAlert(id, next);
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const handleRunCheck = async () => {
    setRunning(true);
    try {
      const { data } = await runAlertsCheck();
      toast.success(`Checked ${data.checked} rules · ${data.fired} fired`);
      load();
    } catch {
      toast.error("Check failed");
    } finally {
      setRunning(false);
    }
  };

  const handleSyncTelegram = async () => {
    setSyncing(true);
    try {
      const { data } = await syncTelegram();
      if (data.auto_linked_count) {
        toast.success(`Linked ${data.auto_linked_count} chat(s) to ${data.auto_linked_to}`);
      } else if (data.found > 0) {
        toast.success(`Synced ${data.found} chat(s)`);
      } else {
        toast.info("No new Telegram messages — message the bot first, then sync.");
      }
      load();
    } catch (err) {
      toast.error("Telegram sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const handleTestTelegram = async () => {
    try {
      const { data } = await sendTelegramTest();
      if (data.sent_to?.length) {
        toast.success(`Test sent to ${data.sent_to.length} chat(s). Check Telegram.`);
      } else {
        toast.error(`Test failed for all ${data.chat_count || 0} chat(s)`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    }
  };

  const handleAutoSetup = async () => {
    if (!window.confirm("Auto-create a starter set of alerts based on your top holdings?\n\nWill create:\n• Price targets ±15% (top 10 holdings)\n• % Move ±5% daily (top 5)\n• Earnings reminders (all direct equity)\n• Score-change alerts (lowest-scoring 5)\n\nExisting rules will NOT be duplicated.")) return;
    setAutoSetting(true);
    try {
      const { data } = await autoSetupAlerts(telegram.linked);
      const { by_type = {}, created_total, skipped_duplicates } = data;
      toast.success(
        `Created ${created_total} rules · ${by_type.price_target || 0} price targets, ${by_type.pct_move || 0} %-move, ${by_type.earnings_reminder || 0} earnings, ${by_type.score_change || 0} score-change` +
        (skipped_duplicates ? ` · skipped ${skipped_duplicates} duplicates` : "")
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-setup failed");
    } finally {
      setAutoSetting(false);
    }
  };

  const handleDeleteAuto = async () => {
    if (!window.confirm("Delete ALL auto-generated alert rules? (Your manually-created rules will be kept.)")) return;
    try {
      const { data } = await deleteAutoAlerts();
      toast.success(`Deleted ${data.deleted} auto-generated rule(s)`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const typeMeta = TYPE_META[type];

  return (
    <div className="p-4 md:p-6 max-w-[1200px] mx-auto space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Alerts</h1>
          <p className="text-sm text-[var(--text-muted)]">Automated % move & earnings reminders — delivered via Telegram.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleAutoSetup}
            disabled={autoSetting}
            className="border-amber-500/40 bg-amber-500/5 text-amber-400 hover:bg-amber-500/10 rounded text-sm"
            data-testid="auto-setup-btn"
          >
            <Wand2 size={15} className={`mr-1.5 ${autoSetting ? "animate-pulse" : ""}`} />
            {autoSetting ? "Setting up…" : "Auto-setup"}
          </Button>
          {rules.some((r) => r.auto_generated) && (
            <Button
              variant="outline"
              onClick={handleDeleteAuto}
              className="border-white/10 text-[var(--text-muted)] hover:bg-white/5 rounded text-sm"
              data-testid="delete-auto-btn"
            >
              <Trash2 size={14} className="mr-1.5" />
              Clear auto
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleRunCheck}
            disabled={running}
            className="border-white/10 text-white hover:bg-white/5 rounded text-sm"
            data-testid="run-check-btn"
          >
            <PlayCircle size={15} className="mr-1.5" /> {running ? "Checking…" : "Run check"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded text-sm" data-testid="new-alert-btn">
                <Plus size={16} className="mr-1" /> New Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[var(--bg-surface)] border border-white/10 text-white max-w-lg" data-testid="alert-dialog">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "Chivo" }}>Create Alert Rule</DialogTitle>
                <DialogDescription className="text-[var(--text-muted)] text-xs">
                  {typeMeta?.description}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Alert Type</label>
                  <Select value={type} onValueChange={onTypeChange}>
                    <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="alert-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                      <SelectItem value="pct_move">% Move (daily)</SelectItem>
                      <SelectItem value="price_target">Price Target (absolute ₹)</SelectItem>
                      <SelectItem value="earnings_reminder">Earnings Reminder</SelectItem>
                      <SelectItem value="score_change">Score Change (Tickertape)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Stock symbol (NSE)</label>
                  <Input
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    placeholder="e.g. RELIANCE, TCS"
                    className="bg-[var(--bg-base)] border-white/10 text-white font-mono"
                    data-testid="alert-symbol"
                  />
                </div>

                {typeMeta?.needs_target_price && (
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-1 block">
                      Target price (₹)
                    </label>
                    <Input
                      type="number"
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(e.target.value)}
                      placeholder="e.g. 2500"
                      step="0.01"
                      min="0"
                      className="bg-[var(--bg-base)] border-white/10 text-white font-mono"
                      data-testid="alert-target-price"
                    />
                  </div>
                )}

                {(typeMeta?.threshold_suffix || typeMeta?.default_threshold !== 0) && type !== "price_target" && type !== "score_change" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[var(--text-muted)] mb-1 block">
                        Threshold{typeMeta?.threshold_suffix}
                      </label>
                      <Input
                        type="number"
                        value={threshold}
                        onChange={(e) => setThreshold(e.target.value)}
                        step="0.5"
                        className="bg-[var(--bg-base)] border-white/10 text-white font-mono"
                        data-testid="alert-threshold"
                      />
                    </div>
                    {typeMeta?.direction && (
                      <div>
                        <label className="text-xs text-[var(--text-muted)] mb-1 block">Direction</label>
                        <Select value={direction} onValueChange={setDirection}>
                          <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="alert-direction">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                            {(typeMeta.direction_options || []).map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}

                {type === "price_target" && typeMeta?.direction && (
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-1 block">Direction</label>
                    <Select value={direction} onValueChange={setDirection}>
                      <SelectTrigger className="bg-[var(--bg-base)] border-white/10" data-testid="alert-direction">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[var(--bg-surface)] border-white/10 text-white">
                        {(typeMeta.direction_options || []).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-xs text-[var(--text-muted)] mb-1 block">Delivery</label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-base)] border border-white/10 rounded">
                    <Send size={14} className="text-[var(--brand)]" />
                    <span className="text-sm text-white">Telegram</span>
                    {telegram.linked ? (
                      <span className="ml-auto flex items-center gap-1 text-[11px] text-[var(--positive,#10B981)]">
                        <Check size={12} /> linked
                      </span>
                    ) : (
                      <span className="ml-auto flex items-center gap-1 text-[11px] text-[var(--warning,#F59E0B)]">
                        <X size={12} /> not linked
                      </span>
                    )}
                  </div>
                  {!telegram.linked && (
                    <p className="text-[10px] text-[var(--warning,#F59E0B)] mt-1.5">
                      Telegram not yet linked — see the Telegram card below to connect.
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleCreate}
                  className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white rounded"
                  data-testid="create-alert-btn"
                >
                  Create rule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Telegram onboarding card */}
      <DigestScheduleCard preferences={preferences} onSaved={load} />
      <TelegramCard
        telegram={telegram}
        onSync={handleSyncTelegram}
        onTest={handleTestTelegram}
        syncing={syncing}
      />

      {/* Active rules */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Bell size={15} className="text-[var(--brand)]" />
          <span className="text-sm font-medium text-white">Active rules</span>
          <span className="text-xs text-[var(--text-muted)] ml-auto">{rules.length} total</span>
        </div>
        {rules.length === 0 ? (
          <div className="p-10 text-center text-[var(--text-muted)] text-sm">
            No rules yet. Create one above to get notified on price moves or earnings.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {rules.map((r) => {
              const meta = TYPE_META[r.type] || { label: r.type, icon: Bell };
              const Icon = meta.icon;
              return (
                <div key={r.id} className="flex items-center justify-between px-5 py-3 hover:bg-white/[0.02]" data-testid={`rule-${r.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded ${r.active ? "bg-[var(--brand)]/10 text-[var(--brand)]" : "bg-white/5 text-[var(--text-muted)]"}`}>
                      <Icon size={15} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm text-white">{r.symbol || "—"}</span>
                        <Badge variant="outline" className="text-[10px] border-white/10 text-[var(--text-muted)]">{meta.label}</Badge>
                        {r.auto_generated && (
                          <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400" data-testid={`auto-badge-${r.id}`}>
                            auto
                          </Badge>
                        )}
                        {r.type === "pct_move" && (
                          <span className="text-xs text-[var(--text-muted)]">
                            ±{r.threshold_pct}% · {r.direction}
                          </span>
                        )}
                        {r.type === "earnings_reminder" && (
                          <span className="text-xs text-[var(--text-muted)]">
                            {r.threshold_pct} days out
                          </span>
                        )}
                        {r.type === "price_target" && (
                          <span className="text-xs text-[var(--text-muted)]">
                            {r.direction} ₹{(r.target_price || 0).toLocaleString("en-IN")}
                          </span>
                        )}
                        {r.type === "score_change" && (
                          <span className="text-xs text-[var(--text-muted)]">
                            watching: {(r.watch_sections || ["entry_point","red_flags"]).join(", ")}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1">
                          {(r.channels || []).map((c) => (
                            <span key={c} className="px-1.5 py-0.5 rounded bg-white/5 text-[10px]">
                              {CHANNEL_LABELS[c] || c}
                            </span>
                          ))}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={10} /> Cooldown {r.cooldown_hours}h
                        </span>
                        {r.last_fired_at && (
                          <span>Last fired: {fmtDate(r.last_fired_at)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(r.id, !r.active)}
                      className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${r.active ? "bg-[var(--brand)]/15 text-[var(--brand)]" : "bg-white/5 text-[var(--text-muted)]"}`}
                      data-testid={`toggle-${r.id}`}
                    >
                      {r.active ? "Active" : "Paused"}
                    </button>
                    <button
                      onClick={() => handleDelete(r.id)}
                      className="p-1.5 hover:bg-[var(--negative)]/10 text-[var(--text-muted)] hover:text-[var(--negative)] rounded transition-colors"
                      data-testid={`del-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Clock size={15} className="text-[var(--brand)]" />
          <span className="text-sm font-medium text-white">Recent triggers</span>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-[var(--text-muted)] text-xs">No alerts fired yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {history.map((h, i) => (
              <div key={i} className="px-5 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{h.subject}</div>
                  <div className="text-[11px] text-[var(--text-muted)]">{fmtDate(h.fired_at)} · {h.symbol}</div>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  {h.delivery?.telegram === true && <Badge variant="outline" className="text-[10px] border-[var(--positive,#10B981)]/30 text-[var(--positive,#10B981)]">TG ✓</Badge>}
                  {h.delivery?.telegram === false && <Badge variant="outline" className="text-[10px] border-[var(--negative)]/30 text-[var(--negative)]">TG ✗</Badge>}
                  {typeof h.delivery?.telegram === "string" && <Badge variant="outline" className="text-[10px] border-[var(--warning,#F59E0B)]/30 text-[var(--warning,#F59E0B)]">{h.delivery.telegram}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DigestScheduleCard({ preferences, onSaved }) {
  const [draft, setDraft] = useState(preferences);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(preferences), [preferences]);
  if (!draft) return null;

  const rows = [
    ["morning", "Morning brief", "Portfolio, risks and the day’s priorities"],
    ["pre_close", "Pre-close action brief", "Only actionable changes before market close"],
    ["weekly", "Weekly review", "A Sunday review of performance and open actions"],
  ];
  const update = (key, patch) =>
    setDraft((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
  const save = async () => {
    setSaving(true);
    try {
      await updateNotificationPreferences({
        timezone: draft.timezone || "Asia/Kolkata",
        morning: draft.morning,
        pre_close: draft.pre_close,
        evening: draft.evening,
        weekly: draft.weekly,
        critical_alerts: draft.critical_alerts !== false,
      });
      toast.success("Notification schedule saved");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-white flex items-center gap-2">
            <Clock size={16} className="text-[var(--brand)]" /> Intelligence schedule
          </div>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            India time · critical alerts remain immediate
          </p>
        </div>
        <div className="grid gap-3 flex-1 max-w-3xl">
          {rows.map(([key, label, detail]) => (
            <div key={key} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-white/5 pb-3">
              <div>
                <div className="text-sm text-white">{label}</div>
                <div className="text-[11px] text-[var(--text-muted)]">{detail}</div>
              </div>
              <Input
                type="time"
                value={`${String(draft[key].hour).padStart(2, "0")}:${String(draft[key].minute).padStart(2, "0")}`}
                onChange={(event) => {
                  const [hour, minute] = event.target.value.split(":").map(Number);
                  update(key, { hour, minute });
                }}
                className="w-28 h-9 bg-[var(--bg-base)] border-white/10"
              />
              <button
                type="button"
                onClick={() => update(key, { enabled: !draft[key].enabled })}
                className={`w-12 h-7 rounded-full p-1 transition-colors ${draft[key].enabled ? "bg-[var(--brand)]" : "bg-white/10"}`}
                aria-label={`${draft[key].enabled ? "Disable" : "Enable"} ${label}`}
              >
                <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${draft[key].enabled ? "translate-x-5" : ""}`} />
              </button>
            </div>
          ))}
          <Button onClick={save} disabled={saving} className="justify-self-end bg-[var(--brand)] text-black hover:bg-[var(--brand-hover)]">
            {saving ? "Saving…" : "Save schedule"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TelegramCard({ telegram, onSync, onTest, syncing }) {
  const botLink = telegram.bot_username ? `https://t.me/${telegram.bot_username}` : null;
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-5" data-testid="telegram-card">
      <div className="flex items-start gap-4 flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 rounded ${telegram.linked ? "bg-[var(--brand)]/15 text-[var(--brand)]" : "bg-white/5 text-[var(--text-muted)]"}`}>
            <Send size={18} />
          </div>
          <div>
            <div className="text-sm font-medium text-white flex items-center gap-2">
              Telegram delivery
              {telegram.linked ? (
                <Badge variant="outline" className="text-[10px] border-[var(--positive,#10B981)]/40 text-[var(--positive,#10B981)]">Connected</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-[var(--warning,#F59E0B)]/40 text-[var(--warning,#F59E0B)]">Not linked</Badge>
              )}
            </div>
            {telegram.linked ? (
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Chat ID{telegram.chat_ids.length > 1 ? "s" : ""}: <span className="font-mono text-white/80">{telegram.chat_ids.join(", ")}</span>
              </p>
            ) : (
              <p className="text-xs text-[var(--text-muted)] mt-1 max-w-xl leading-relaxed">
                1. Open <a href={botLink || "#"} target="_blank" rel="noreferrer" className="text-[var(--brand)] underline font-mono">@{telegram.bot_username || "Capital360amansaluja_bot"}</a>
                <br />
                2. Send <span className="font-mono bg-white/5 px-1 rounded">/start</span>  (or any message)
                <br />
                3. Click <b>Sync Telegram</b> below
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {telegram.linked && (
            <Button
              variant="outline"
              onClick={onTest}
              className="border-[var(--brand)]/40 text-[var(--brand)] hover:bg-[var(--brand)]/10 rounded text-xs"
              data-testid="test-tg-btn"
            >
              <Send size={13} className="mr-1.5" />
              Send test
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onSync}
            disabled={syncing}
            className="border-white/10 text-white hover:bg-white/5 rounded text-xs"
            data-testid="sync-tg-btn"
          >
            <RefreshCcw size={13} className={`mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Telegram"}
          </Button>
        </div>
      </div>
    </div>
  );
}
